import { Box, Stack, Tab, Typography } from '@montugroup/design-system';
import { TabContext, TabPanel } from '@mui/lab';
import { Tabs } from '@mui/material';

import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { BRAZE_CONTENT_CARD } from '@/assets/data/enums';
import BackButton from '@/components/buttons/BackButton';
import { ErrorAlert, InfoAlert } from '@/components/common/RebrandStyledComponents';
import useGetConsultations from '@/hooks/consultations/useGetConsultations';
import useGetPatientDoctorAvailability from '@/hooks/patient/useGetPatientDoctorAvailability';
import useGetPatientOrderSummary from '@/hooks/patient/useGetPatientOrderSummary';
import usePagination from '@/hooks/usePagination';
import BookConsultationCard from '@/pages/consultations/components/BookConsultationCard';
import FAQAccordion from '@/pages/consultations/components/FAQAccordion';
import HistoryConsultationCardSkeleton from '@/pages/consultations/components/HistoryCardLoadingSkeleton';
import HistoryConsultationCard from '@/pages/consultations/components/HistoryConsultationCard';
import { UpcomingConsultationCard } from '@/pages/consultations/components/UpcomingConsultationCard';
import { useBookingCompletedPrompt } from '@/pages/consultations/hooks/useBookingCompletedPrompt';
import { SinglePageContentCard } from '@montugroup/circuit-shared/components/braze/SinglePageContentCard';

import FAQConsultationData from '../patient/umeds_rebrand/FAQConsultationData';

import { BrazeImageCard } from '@/components/braze/BrazeImageCard';
import { FF_BRAZE_CONTENT_CARDS } from '@/constants/featureFlags';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import useUser from '@montugroup/circuit-shared/hooks/user/useUser';
import type { TypedAnalyticsContexts } from '@montugroup/data-collection';
import { AnalyticsTrackingProvider, buildContextId } from '@montugroup/data-collection';
import ConsultationPagination from './components/ConsultationPagination';
import { PostBookingWaitlistDialog } from './components/PostBookingWaitlistDialog';
import UpcomingConsultationCardSkeleton from './components/UpcomingConsultationLoadingSkeleton';
import { ContentWrapper, FAQContainer } from './PatientConsultation.styles';
import buildDoctorBookingLink, { canPatientBookNewDoctor } from './utils';

const tabContextMap: Record<number, 'upcoming' | 'history'> = {
  0: 'upcoming',
  1: 'history',
};

export default function PatientConsultation() {
  const { data: user } = useUser();
  const [value, setValue] = useState(0);
  const { flags } = useFeatureFlags();

  const { consultData, isLoading, isError, isSuccess, refetch } = useGetConsultations({
    when: 'upcoming',
  });

  const { prompt: postBookingPrompt, close: closePostBookingPrompt } = useBookingCompletedPrompt();

  const {
    consultData: completedData,
    isLoading: isLoadingCompleted,
    isError: isErrorCompleted,
    isSuccess: isSuccessCompleted,
  } = useGetConsultations({
    when: 'completed',
  });

  const {
    data: bookingData,
    isLoading: fetchingAvailability,
    isError: isBookingError,
  } = useGetPatientDoctorAvailability(user?.id);

  const [expandedHistoryConsultations, setExpandedHistoryConsultations] = useState<boolean[]>(
    Array(completedData?.length || 0).fill(false),
  );

  const bookingLink = useMemo(() => {
    return buildDoctorBookingLink(bookingData?.calcomUsername, bookingData?.calcomFollowUpConsultEvent);
  }, [bookingData]);

  const currentDoctorIsUnavailable = bookingData?.followUpAvailabilityStatus === 'Unavailable';

  // Check if patient has any active open orders
  const { isLoading: fetchingOrderSummary, data: openOrdersResponse } = useGetPatientOrderSummary();

  const showDoctorFollowupRoundRobinButton =
    !fetchingAvailability &&
    !fetchingOrderSummary &&
    canPatientBookNewDoctor(currentDoctorIsUnavailable, openOrdersResponse?.openOrdersCount);

  const onTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  useEffect(() => {
    if (isSuccessCompleted && completedData.length > 0) {
      const initialExpandedState = completedData.map((_, index) => index < 2);
      setExpandedHistoryConsultations(initialExpandedState);
    }
  }, [completedData, isSuccessCompleted]);

  const [handlePagination, currentPage, filterData, totalPageCount] = usePagination({
    data: completedData,
    search: null,
  });

  const toggleExpanded = (index: number) => {
    setExpandedHistoryConsultations((prevState) => {
      const consultationState = [...prevState];
      consultationState[index] = !consultationState[index];
      return consultationState;
    });
  };

  // Adds active tab context ID
  const updateTrackingContext = useCallback(
    (ctx: TypedAnalyticsContexts): TypedAnalyticsContexts => {
      const contextId = buildContextId('consultations', tabContextMap[value] ?? `unmapped_tab_${value}`);
      return {
        ...ctx,
        uiEvent: {
          ...ctx.uiEvent,
          context_id: contextId,
        },
      };
    },
    [value],
  );

  const renderHistoryConsultations = useMemo(() => {
    if (isLoadingCompleted) {
      return (
        <>
          <HistoryConsultationCardSkeleton isExpanded />
          <HistoryConsultationCardSkeleton isExpanded />
        </>
      );
    }
    if (isErrorCompleted) {
      return (
        <ErrorAlert variant="standard" severity="error">
          An unexpected error occurred. Please try again.
        </ErrorAlert>
      );
    }
    if (isSuccessCompleted && completedData.length === 0) {
      return <InfoAlert severity="info">No consultations found.</InfoAlert>;
    }

    return (
      <>
        {filterData.map((consultation, index) => (
          <HistoryConsultationCard
            key={consultation.id}
            consultation={consultation}
            isExpanded={expandedHistoryConsultations[index] || false}
            toggleExpanded={() => toggleExpanded(index)}
          />
        ))}
        {filterData.length > 0 && (
          <ConsultationPagination
            totalPageCount={totalPageCount}
            currentPage={currentPage}
            handlePagination={handlePagination}
          />
        )}
      </>
    );
  }, [
    completedData.length,
    currentPage,
    expandedHistoryConsultations,
    filterData,
    handlePagination,
    isErrorCompleted,
    isLoadingCompleted,
    isSuccessCompleted,
    totalPageCount,
  ]);

  const renderUpcomingConsultations = useMemo(() => {
    // only on the first load: a background refetch (after a waitlist change, or while finding a new booking) keeps the
    // cards mounted, so their state and focus survive
    if (isLoading) {
      return <UpcomingConsultationCardSkeleton />;
    }
    if (isError) {
      return (
        <ErrorAlert variant="standard" severity="error">
          An unexpected error occurred. Please try again.
        </ErrorAlert>
      );
    }
    if (isSuccess && consultData.length === 0) {
      if (isBookingError) {
        return (
          <ErrorAlert variant="standard" severity="error">
            An unexpected error occurred. Please try again.
          </ErrorAlert>
        );
      }

      return (
        <BookConsultationCard
          link={bookingLink}
          price="$29"
          refetch={refetch}
          showDoctorFollowupRoundRobinButton={showDoctorFollowupRoundRobinButton}
        />
      );
    }

    return (
      <Stack gap={4}>
        {consultData.map((consultation) => (
          <UpcomingConsultationCard key={consultation.id} consultation={consultation} refetchConsultations={refetch} />
        ))}
      </Stack>
    );
  }, [
    consultData,
    isError,
    isLoading,
    isSuccess,
    bookingLink,
    isBookingError,
    refetch,
    showDoctorFollowupRoundRobinButton,
  ]);

  return (
    <AnalyticsTrackingProvider update={updateTrackingContext}>
      <Box display="flex">
        <BackButton />
        <Typography
          variant="h5"
          component="h1"
          sx={(theme) => ({ color: theme.palette.primary.main })}
          lineHeight={(theme) => theme.spacing(10)}
        >
          Consultations
        </Typography>
      </Box>

      <Box sx={{ '& > :first-of-type': { margin: (theme) => `${theme.spacing(6)} 0` } }}>
        <SinglePageContentCard displayType={BRAZE_CONTENT_CARD.PATIENT_CONSULTATIONS} align="left" />
      </Box>

      <Tabs
        value={value}
        onChange={onTabChange}
        aria-label="Patient consultation tabs"
        sx={{ marginTop: (theme) => theme.spacing(5) }}
      >
        <Tab
          id="tab-upcoming"
          label="Upcoming"
          aria-controls="tabpanel-0"
          sx={(theme) => ({
            [theme.breakpoints.down('md')]: {
              width: '50%',
            },
          })}
        />
        <Tab
          id="tab-history"
          label="History"
          aria-controls="tabpanel-1"
          sx={(theme) => ({
            [theme.breakpoints.down('md')]: {
              width: '50%',
            },
          })}
        />
      </Tabs>
      <TabContext value={value.toString()}>
        <TabPanel id="tabpanel-0" value="0">
          {flags[FF_BRAZE_CONTENT_CARDS] ? (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
              {renderUpcomingConsultations}
              <BrazeImageCard />
            </Stack>
          ) : (
            renderUpcomingConsultations
          )}
        </TabPanel>
        <TabPanel id="tabpanel-1" value="1">
          {renderHistoryConsultations}
        </TabPanel>
      </TabContext>
      <FAQContainer>
        <ContentWrapper>
          <Typography
            variant="subtitle2"
            component="h2"
            color="primary.main"
            lineHeight={(theme) => theme.spacing(8)}
            fontWeight={600}
            marginBottom={(theme) => theme.spacing(4)}
          >
            FAQ
          </Typography>
          <Stack gap={(theme) => theme.spacing(2)} maxWidth="lg">
            {FAQConsultationData.map((faq: { id: number; question: string; content: ReactNode }) => (
              <FAQAccordion key={faq.id} question={faq.question} content={faq.content} />
            ))}
          </Stack>
        </ContentWrapper>
      </FAQContainer>
      {postBookingPrompt && (
        <PostBookingWaitlistDialog
          key={postBookingPrompt.key}
          prompt={postBookingPrompt}
          onClose={closePostBookingPrompt}
        />
      )}
    </AnalyticsTrackingProvider>
  );
}

PatientConsultation.displayName = 'PatientConsultation';
